/**
 * iter-119aa (P1) — Super Admin scope filter pill.
 *
 * Rendered in the global TopBar; only visible when the auth user is SA.
 * Two cascading dropdowns: Tenant → Org. "All" preserves the current
 * union-scope behaviour so SA's default view doesn't change.
 *
 * Selection writes to URL params via the context provider; every page
 * that consumes `useScopeFilter()` picks up the change without a
 * router push.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { useScopeFilter } from "@/lib/scope-filter-context";
import { Globe, Building2, Layers } from "lucide-react";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function ScopeFilterPill() {
    const { user } = useAuth();
    const { tenantId, orgId, isSA, setScope } = useScopeFilter();
    const [tenants, setTenants] = useState([]);
    const [orgs, setOrgs] = useState([]);

    useEffect(() => {
        if (!isSA) return;
        // Load tenants + orgs once. These are tiny lists for SA's reach.
        // iter-119ac fix: tenants list lives at /operations/tenants (SA-only
        // surface). The previous `/tenants` route never existed → silent 404
        // → empty dropdown → user couldn't pick another tenant.
        Promise.allSettled([
            api.get("/operations/tenants"),
            api.get("/organizations"),
        ]).then(([t, o]) => {
            if (t.status === "fulfilled") {
                const rows = t.value.data?.data || t.value.data || [];
                // operations endpoint may return either a flat list or an envelope.
                setTenants(Array.isArray(rows) ? rows : (rows.tenants || []));
            }
            if (o.status === "fulfilled") setOrgs(o.value.data?.data || o.value.data || []);
        });
    }, [isSA]);

    if (!isSA || !user) return null;

    const orgsForTenant = tenantId
        ? orgs.filter((o) => o.tenant_id === tenantId)
        : orgs;
    const tenantLabel = tenants.find((t) => t.id === tenantId)?.name || "All tenants";
    const orgLabel = orgs.find((o) => o.id === orgId)?.name || "All orgs";
    const active = tenantId || orgId;

    return (
        <div className="flex items-center gap-1.5" data-testid="sa-scope-pill">
            {/* Tenant dropdown */}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        className={`h-8 px-2.5 inline-flex items-center gap-1.5 border rounded-sm text-[11px] font-mono uppercase tracking-wider transition-colors ${
                            tenantId ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-foreground/50"
                        }`}
                        data-testid="sa-scope-tenant-btn"
                        title="SA scope — tenant"
                    >
                        <Building2 className="h-3 w-3" />
                        <span className="truncate max-w-[100px]">{tenantLabel}</span>
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[220px]">
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">Tenant scope</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        onClick={() => setScope({ tenantId: null, orgId: null })}
                        data-testid="sa-scope-tenant-all"
                    >
                        <Globe className="h-3.5 w-3.5 mr-2" /> All tenants (union)
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {tenants.map((t) => (
                        <DropdownMenuItem
                            key={t.id}
                            onClick={() => setScope({ tenantId: t.id, orgId: null })}
                            data-testid={`sa-scope-tenant-${t.id}`}
                        >
                            <Building2 className="h-3.5 w-3.5 mr-2" />
                            {t.name}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>

            {/* Org dropdown */}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        className={`h-8 px-2.5 inline-flex items-center gap-1.5 border rounded-sm text-[11px] font-mono uppercase tracking-wider transition-colors ${
                            orgId ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-foreground/50"
                        }`}
                        data-testid="sa-scope-org-btn"
                        title="SA scope — org"
                    >
                        <Layers className="h-3 w-3" />
                        <span className="truncate max-w-[100px]">{orgLabel}</span>
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[220px]">
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">Org scope</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        onClick={() => setScope({ orgId: null })}
                        data-testid="sa-scope-org-all"
                    >
                        <Globe className="h-3.5 w-3.5 mr-2" /> All orgs
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {orgsForTenant.map((o) => (
                        <DropdownMenuItem
                            key={o.id}
                            onClick={() => setScope({ orgId: o.id })}
                            data-testid={`sa-scope-org-${o.id}`}
                        >
                            <Layers className="h-3.5 w-3.5 mr-2" />
                            {o.name}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>

            {active && (
                <button
                    onClick={() => setScope({ tenantId: null, orgId: null })}
                    className="h-8 w-8 inline-flex items-center justify-center border border-border rounded-sm hover:bg-secondary text-[10px]"
                    title="Clear SA scope"
                    data-testid="sa-scope-clear"
                >
                    ✕
                </button>
            )}
        </div>
    );
}
